import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import supabase from '../utils/supabaseClient';
import { getCatalogueMode } from '../data/settings';

// App-wide global settings (currently just Catalogue Mode).
// Fails open: if the flag can't be read, catalogueMode stays false and the site
// behaves exactly as it did before this feature.

const SettingsContext = createContext({
  catalogueMode: false,
  loading: true,
  refresh: () => {},
});

export function SettingsProvider({ children }) {
  const [catalogueMode, setCatalogueMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setCatalogueMode(await getCatalogueMode());
    } catch (err) {
      console.error('Error loading app settings:', err);
      setCatalogueMode(false); // fail open — prices shown
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live-update open tabs when an admin toggles the flag. Best-effort: if
  // realtime isn't available the flag still updates on the next page load.
  useEffect(() => {
    let channel;
    try {
      channel = supabase
        .channel('app_settings_changes')
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'app_settings' },
          () => refresh()
        )
        .subscribe();
    } catch (err) {
      console.error('Could not subscribe to app_settings changes:', err);
    }
    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [refresh]);

  return (
    <SettingsContext.Provider value={{ catalogueMode, loading, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}
